import React, { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, ShoppingBag, Users, UserPlus, Trophy, BarChart3, Package, Tag, Percent, XCircle, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const Evo = ({ pct }) => {
  if (pct == null) return <span className="text-zinc-600 text-xs">—</span>;
  const up = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold ${up ? "text-neon-green" : "text-red-400"}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}{up ? "+" : ""}{pct}%
    </span>
  );
};

const Card = ({ label, value, sub, icon: Icon, color = "text-white" }) => (
  <div className="bg-ink-surface border border-zinc-800 p-4">
    <p className="text-zinc-500 text-xs uppercase mb-1 flex items-center gap-1">{Icon && <Icon className="h-3.5 w-3.5" />} {label}</p>
    <p className={`font-anton text-xl ${color}`}>{value}</p>
    {sub && <div className="text-zinc-500 text-xs mt-1">{sub}</div>}
  </div>
);

const VendorTable = ({ vendors }) => (
  <div className="bg-ink-surface border border-zinc-800 overflow-x-auto">
    <table className="w-full text-xs sm:text-sm">
      <thead><tr className="text-zinc-600 text-xs uppercase text-left border-b border-zinc-800">
        <th className="p-2">Vendeur</th><th className="text-right">Livraisons</th><th className="text-right">Refusées</th>
        <th className="text-right">CA usine</th><th className="text-right">Marge vendeur</th><th className="text-right">TVA</th>
        <th className="text-right">Panier moy.</th><th className="text-right">Nouv. clients</th><th className="text-right">Fidèles</th>
        <th className="text-right">Cadeaux</th><th className="text-right">Points</th><th className="text-right pr-2">En ligne</th>
      </tr></thead>
      <tbody>
        {vendors.map((v) => (
          <tr key={v.vendor_id} data-testid="report-vendor-row" className="border-b border-zinc-800/60 text-zinc-300">
            <td className="p-2 text-white font-bold">{v.vendor_name}<span className="text-zinc-600 font-normal"> · {v.city}</span></td>
            <td className="text-right">{v.deliveries}</td>
            <td className={`text-right ${v.refused > 0 ? "text-red-400" : "text-zinc-600"}`}>{v.refused}</td>
            <td className="text-right text-neon-green font-bold">{v.ca_usine.toFixed(2)}€</td>
            <td className="text-right">{v.ca_vendor.toFixed(2)}€</td>
            <td className="text-right text-zinc-500">{v.tva.toFixed(2)}€</td>
            <td className="text-right">{v.avg_basket.toFixed(2)}€</td>
            <td className="text-right">{v.new_clients}</td>
            <td className="text-right">{v.loyal_clients}</td>
            <td className="text-right">{v.gifts}</td>
            <td className="text-right text-neon-green">{v.points}</td>
            <td className="text-right pr-2 text-zinc-400">{v.online_hours}h / {v.online_days}j</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const downloadPdf = async (weekStart) => {
  try {
    const res = await api.get(`/admin/weekly-report/pdf?week_start=${weekStart}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `bilan-${weekStart}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Erreur lors de la génération du PDF"); }
};

const WeekDetail = ({ w, title }) => (
  <section>
    <h2 className="font-anton text-xl uppercase text-white mb-3 flex items-center gap-2">
      <BarChart3 className="h-5 w-5 text-neon-magenta" /> {title} — {w.label}
      {w.in_progress && <span className="text-xs uppercase border border-neon-green text-neon-green px-2 py-0.5">En cours</span>}
      <button data-testid="report-pdf-btn" onClick={() => downloadPdf(w.week_start)} className="ml-auto flex items-center gap-1 text-xs uppercase font-bold border border-zinc-700 text-zinc-300 px-3 py-1.5 hover:border-neon-magenta hover:text-neon-magenta transition-colors"><FileText className="h-4 w-4" /> Exporter en PDF</button>
    </h2>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
      <Card label="CA usine" value={`${w.ca_usine.toFixed(2)}€`} sub={<Evo pct={w.evolution_pct} />} color="text-neon-green" />
      <Card label="Commandes livrées" value={w.orders_count} icon={ShoppingBag} sub={w.refused_count > 0 ? <span className="text-red-400">{w.refused_count} refusée{w.refused_count > 1 ? "s" : ""}</span> : null} />
      <Card label="Panier moyen" value={`${w.avg_order.toFixed(2)}€`} />
      <Card label="Nouveaux clients" value={w.new_clients} icon={Users} />
      <Card label="Nouveaux vendeurs" value={w.new_vendors.length} icon={UserPlus} sub={w.new_vendors.length > 0 ? w.new_vendors.join(", ") : null} />
      <Card label="Top vendeur" value={w.top_vendor ? w.top_vendor.name : "—"} icon={Trophy} color="text-yellow-400" sub={w.top_vendor ? `${w.top_vendor.ca.toFixed(2)}€ usine` : null} />
      <Card label="Produit le + vendu" value={w.top_product ? w.top_product.name : "—"} icon={Package} sub={w.top_product ? `${w.top_product.qty} unités` : null} />
      <Card label="Catégorie la + vendue" value={w.top_category ? w.top_category.name : "—"} icon={Tag} sub={w.top_category ? `${w.top_category.qty} unités` : null} />
      <Card label="Pertes promos admin" value={`-${w.admin_promo_loss.toFixed(2)}€`} icon={Percent} color={w.admin_promo_loss > 0 ? "text-red-400" : "text-zinc-500"} sub="absorbées par le gain usine" />
      <Card label="Commandes refusées" value={w.refused_count} icon={XCircle} color={w.refused_count > 0 ? "text-red-400" : "text-white"} />
    </div>
    {w.vendors.length > 0 ? (
      <>
        <p className="text-zinc-500 text-xs uppercase mb-2">Bilan par vendeur</p>
        <VendorTable vendors={w.vendors} />
      </>
    ) : <p className="text-zinc-600 text-sm">Aucune livraison cette semaine.</p>}
  </section>
);

export const AdminWeeklyReport = () => {
  const [weeks, setWeeks] = useState(null);
  useEffect(() => { api.get("/admin/weekly-report").then((r) => setWeeks(r.data.weeks)).catch(console.error); }, []);
  if (!weeks) return <p className="text-zinc-500 py-10 text-center">Chargement...</p>;

  return (
    <div className="space-y-8" data-testid="admin-weekly-report">
      <WeekDetail w={weeks[0]} title="Semaine en cours" />
      {weeks[1] && <WeekDetail w={weeks[1]} title="Bilan semaine dernière" />}

      <section>
        <h2 className="font-anton text-xl uppercase text-white mb-3">Historique (12 semaines)</h2>
        <div className="bg-ink-surface border border-zinc-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-zinc-600 text-xs uppercase text-left border-b border-zinc-800">
              <th className="p-3">Semaine</th><th className="text-right">CA usine</th><th className="text-right">Évol.</th>
              <th className="text-right">Cmd</th><th className="text-right">Panier moy.</th>
              <th className="text-right">Nouv. clients</th><th className="text-right">Top produit</th><th className="text-right">Top vendeur</th><th className="text-right pr-3">PDF</th>
            </tr></thead>
            <tbody>
              {weeks.map((w) => (
                <tr key={w.week_start} data-testid="weekly-report-row" className="border-b border-zinc-800/60 text-zinc-300">
                  <td className="p-3 text-white">{w.label}{w.in_progress && <span className="ml-2 text-xs text-neon-green">(en cours)</span>}</td>
                  <td className="text-right text-neon-green font-bold">{w.ca_usine.toFixed(2)}€</td>
                  <td className="text-right"><Evo pct={w.evolution_pct} /></td>
                  <td className="text-right">{w.orders_count}</td>
                  <td className="text-right">{w.avg_order.toFixed(2)}€</td>
                  <td className="text-right">{w.new_clients}</td>
                  <td className="text-right text-zinc-400">{w.top_product ? w.top_product.name : "—"}</td>
                  <td className="text-right text-zinc-400">{w.top_vendor ? w.top_vendor.name : "—"}</td>
                  <td className="text-right pr-3"><button data-testid="history-pdf-btn" onClick={() => downloadPdf(w.week_start)} className="p-1 text-zinc-500 hover:text-neon-magenta"><FileText className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};
